// Staff-side firm billing. Two ledgers arrive separately and stay that
// way: the spreadsheet summary in dollars, online invoices/payments in
// cents. Deliberately no fixture fallback.

import { downloadStaffFile, getAuthHeaders } from "../../../api/auth";

export interface BillingSummary {
  totalDollars: number | null;
  paidDollars: number | null;
  balanceDollars: number | null;
  casesWithFinancials: number;
  syncedAt: string | null;
}

export interface BillingSummaryResponse {
  configured: boolean;
  summary: BillingSummary | null;
}

export interface FirmInvoiceRow {
  _id: string;
  number?: string | null;
  description: string;
  amountCents: number;
  status: "open" | "paid" | "void";
  dueDate?: string | null;
  createdAt?: string;
  clientLabel: string | null;
}

export interface FirmPaymentRow {
  _id: string;
  amountCents: number;
  provider: "stripe" | "manual";
  /** Manual payments only: how the money physically arrived. */
  method?: "cash" | "check" | "zelle" | "other" | null;
  reference?: string | null;
  status: "pending" | "succeeded" | "expired" | "refunded" | "failed";
  /** Open means the cardholder is contesting the charge; act in Stripe. */
  disputeStatus?: "open" | "won" | "lost" | null;
  /** The processor's account mode. Only false is a claim - money taken by
      hand and rows older than the stamp carry nothing. */
  livemode?: boolean | null;
  receiptUrl?: string | null;
  /** Online payments: the number on Stripe's own invoice (carries ours). */
  providerInvoiceNumber?: string | null;
  paidAt?: string | null;
  createdAt?: string;
  clientLabel: string | null;
  invoiceNumber: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export function fetchBillingSummary(): Promise<BillingSummaryResponse> {
  return getJson<BillingSummaryResponse>("/api/billing/summary");
}

export type InvoiceSortKey = "createdAt" | "amountCents" | "status";
export type PaymentSortKey = "date" | "amountCents" | "status";
export interface LedgerSort<K extends string> {
  key: K;
  dir: "asc" | "desc";
}

export function fetchBillingInvoices(
  sort: LedgerSort<InvoiceSortKey>,
): Promise<{ total: number; invoices: FirmInvoiceRow[] }> {
  return getJson(`/api/billing/invoices?limit=50&sort=${sort.key}&dir=${sort.dir}`);
}

/** A payment as it appears inside its invoice's detail. */
export interface FirmInvoicePaymentRow {
  _id: string;
  provider: "stripe" | "manual";
  method?: "cash" | "check" | "zelle" | "other" | null;
  reference?: string | null;
  note?: string | null;
  status: "pending" | "succeeded" | "expired" | "refunded" | "failed";
  disputeStatus?: "open" | "won" | "lost" | null;
  receiptUrl?: string | null;
  providerInvoiceNumber?: string | null;
  amountCents: number;
  paidAt?: string | null;
  createdAt?: string;
  recordedByName: string | null;
}

export interface FirmInvoiceDetail {
  invoice: FirmInvoiceRow & {
    caseId: string;
    currency?: string;
    notifiedAt?: string | null;
    issuedByName: string | null;
  };
  payments: FirmInvoicePaymentRow[];
}

export interface FirmPaymentDetail {
  payment: FirmPaymentRow & {
    caseId: string;
    invoiceId: string;
    note?: string | null;
    refundedAt?: string | null;
    invoiceDescription: string | null;
    recordedByName: string | null;
    reversedByName: string | null;
  };
}

async function getJsonOrNull<T>(path: string): Promise<T | null> {
  const res = await fetch(path, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  // A missing or malformed id renders as "not found", not as a crash.
  if (res.status === 404 || res.status === 400) return null;
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export function fetchBillingInvoice(id: string): Promise<FirmInvoiceDetail | null> {
  return getJsonOrNull<FirmInvoiceDetail>(`/api/billing/invoices/${encodeURIComponent(id)}`);
}

export function fetchBillingPayment(id: string): Promise<FirmPaymentDetail | null> {
  return getJsonOrNull<FirmPaymentDetail>(`/api/billing/payments/${encodeURIComponent(id)}`);
}

export function fetchBillingPayments(
  sort: LedgerSort<PaymentSortKey>,
): Promise<{ total: number; payments: FirmPaymentRow[] }> {
  return getJson(`/api/billing/payments?limit=50&sort=${sort.key}&dir=${sort.dir}`);
}

export interface FirmPlanRow {
  _id: string;
  caseId: string;
  clientLabel: string | null;
  totalCents: number;
  installmentCount: number;
  paidCount: number;
  nextDueDate: string | null;
  nextAmountCents: number | null;
}

export function fetchBillingPlans(): Promise<{ plans: FirmPlanRow[] }> {
  return getJson("/api/billing/plans");
}

// One client's money across every matter. Rows carry the matter they
// belong to because one person's bills can span several cases.
export interface ClientInvoiceRow {
  _id: string;
  number?: string | null;
  description: string;
  amountCents: number;
  status: "open" | "paid" | "void";
  dueDate?: string | null;
  createdAt?: string;
  caseId: string;
  caseEId: string | null;
}

export interface ClientPlanRow {
  _id: string;
  caseId: string;
  caseEId: string | null;
  totalCents: number;
  status: "active" | "completed" | "cancelled";
  installmentCount: number;
  issuedCount: number;
  paidCount: number;
  nextDueDate: string | null;
  nextAmountCents: number | null;
}

export interface ClientBillingResponse {
  invoices: ClientInvoiceRow[];
  plans: ClientPlanRow[];
}

// Throws on failure like every money read: a bill that cannot be fetched
// must surface as an error, never as "nothing billed".
export function fetchClientBilling(clientId: string): Promise<ClientBillingResponse> {
  return getJson(`/api/clients/${encodeURIComponent(clientId)}/billing`);
}

export interface CollectedMonth {
  month: string;
  stripeCents: number;
  manualCents: number;
  collectedCents: number;
  returnedCents: number;
  netCents: number;
}

export interface CollectedReport {
  from: string;
  to: string;
  months: CollectedMonth[];
  totals: Omit<CollectedMonth, "month">;
}

export function fetchCollectedReport(from: string, to: string): Promise<CollectedReport> {
  return getJson(`/api/billing/reports/collected?from=${from}&to=${to}`);
}

export interface CollectedDay extends Omit<CollectedMonth, "month"> {
  day: string;
}

export interface CollectedDailyReport {
  from: string;
  to: string;
  days: CollectedDay[];
  totals: Omit<CollectedMonth, "month">;
}

export function fetchCollectedDailyReport(from: string, to: string): Promise<CollectedDailyReport> {
  return getJson(`/api/billing/reports/collected?from=${from}&to=${to}&bucket=day`);
}

export function downloadCollectedCsv(from: string, to: string): Promise<void> {
  return downloadStaffFile(
    `/api/billing/reports/collected.csv?from=${from}&to=${to}`,
    `collected-${from}-${to}.csv`,
    "CSV",
  );
}

export async function downloadReceiptPdf(
  paymentId: string,
  invoiceNumber?: string | null,
): Promise<void> {
  return downloadStaffFile(
    `/api/billing/payments/${encodeURIComponent(paymentId)}/receipt.pdf`,
    `receipt-${invoiceNumber ?? "payment"}.pdf`,
  );
}

/** Admin-only: undo a mistakenly recorded manual payment. */
export async function reverseManualPayment(paymentId: string): Promise<void> {
  const res = await fetch(`/api/billing/payments/${paymentId}/reverse`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Reverse failed (${res.status})`);
  }
}
